import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { DashboardService } from '../../services/dashboard/dashboard';
import { DashboardSummary } from '../../models/dashboard-summary';
import { ToastrService } from 'ngx-toastr';
import { LicenceValidationLog } from '../../models/licence-validation-log';
import { AuditLog } from '../../models/audit-log';
import { ValidationLogsService } from '../../services/validation-logs/validation-logs';
import { AuditLogsService } from '../../services/audit-logs/audit-logs';
import { ExpiringLicence } from '../../models/expiring-licence';
import { LicencesService } from '../../services/licences/licences';
import { AuthService } from '../../services/auth/auth';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.html',
  standalone: false,
  styleUrls: ['./dashboard.css']
})
export class Dashboard implements OnInit {
  loading = false;
  errorMessage = '';

  summary: DashboardSummary = this.createEmptySummary();

  private readonly summaryKeys: Array<keyof DashboardSummary> = [
    'totalOrganizations',
    'activeOrganizations',
    'suspendedOrganizations',
    'disabledOrganizations',
    'totalLicences',
    'activeLicences',
    'expiredLicences',
    'suspendedLicences',
    'revokedLicences',
    'expiringIn30Days',
    'totalApiCredentials',
    'activeApiCredentials',
    'revokedApiCredentials',
    'totalValidationRequests',
    'successfulValidationRequests',
    'failedValidationRequests',
    'pendingPasswordResetRequests'
  ];

  private createEmptySummary(): DashboardSummary {
    return {
    totalOrganizations: 0,
    activeOrganizations: 0,
    suspendedOrganizations: 0,
    disabledOrganizations: 0,

    totalLicences: 0,
    activeLicences: 0,
    expiredLicences: 0,
    suspendedLicences: 0,
    revokedLicences: 0,
    expiringIn30Days: 0,

    totalApiCredentials: 0,
    activeApiCredentials: 0,
    revokedApiCredentials: 0,

    totalValidationRequests: 0,
    successfulValidationRequests: 0,
    failedValidationRequests: 0,
    pendingPasswordResetRequests: 0
    };
  }

  recentValidationLogs: LicenceValidationLog[] = [];
  recentAuditLogs: AuditLog[] = [];

  expiringLicences: ExpiringLicence[] = [];

  constructor(
    private dashboardService: DashboardService,
    private validationLogsService: ValidationLogsService,
    private auditLogsService: AuditLogsService,
    private licencesService: LicencesService,
    private authService: AuthService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadSummary();
    this.loadRecentValidationLogs();
    this.loadRecentAuditLogs();
    this.loadExpiringLicences();
  }

  get isSuperAdmin(): boolean {
    return this.authService.isSuperAdmin();
  }

  loadRecentValidationLogs(): void {
    this.validationLogsService.getLogs(1, 5, '', '', '').subscribe({
      next: response => {
        if (response.success && Array.isArray(response.data)) {
          this.recentValidationLogs = response.data;
          this.cdr.detectChanges();
        }
      },
      error: () => this.cdr.detectChanges()
    });
  }

  loadRecentAuditLogs(): void {
    this.auditLogsService.getLogs(1, 5, '', '', '', '').subscribe({
      next: response => {
        if (response.success && Array.isArray(response.data)) {
          this.recentAuditLogs = response.data;
          this.cdr.detectChanges();
        }
      },
      error: () => this.cdr.detectChanges()
    });
  }

  refreshDashboard(): void {
    this.loadSummary();
    this.loadRecentValidationLogs();
    this.loadRecentAuditLogs();
    this.loadExpiringLicences();
  }

  loadExpiringLicences(): void {
    this.licencesService.getExpiringSoon(30).subscribe({
      next: response => {
        if (response.success && Array.isArray(response.data)) {
          this.expiringLicences = response.data;
          this.cdr.detectChanges();
        }
      },
      error: () => this.cdr.detectChanges()
    });
  }

  loadSummary(): void {
    this.loading = true;
    this.errorMessage = '';

    this.dashboardService.getSummary().subscribe({
      next: response => {
        this.loading = false;

        if (!response.success || !response.data) {
          this.toastr.error(
            response.message,
            'Dashboard'
          );
          this.cdr.detectChanges();
          return;
        }

        this.summary = this.normalizeSummary(response.data);
        this.cdr.detectChanges();
      },
      error: error => {
        this.loading = false;
        this.errorMessage =
          error?.error?.message ||
          error?.error?.error?.details ||
          'Unable to load dashboard summary.';
        this.cdr.detectChanges();
      }
    });
  }

  private normalizeSummary(data: unknown): DashboardSummary {
    const payload = this.extractSummaryPayload(data);
    const normalized = this.createEmptySummary();

    for (const key of this.summaryKeys) {
      normalized[key] = this.readNumber(payload, key);
    }

    return normalized;
  }

  private extractSummaryPayload(data: unknown): Record<string, unknown> {
    if (!data || typeof data !== 'object') {
      return {};
    }

    const payload = data as Record<string, unknown>;
    const nestedKeys = ['summary', 'dashboardSummary', 'dashboard', 'counts', 'data'];

    for (const key of nestedKeys) {
      const value = payload[key];

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value as Record<string, unknown>;
      }
    }

    return payload;
  }

  private readNumber(payload: Record<string, unknown>, key: keyof DashboardSummary): number {
    const candidates = [
      key,
      this.toPascalCase(key),
      this.toSnakeCase(key),
      this.toKebabCase(key)
    ];

    for (const candidate of candidates) {
      const value = payload[candidate];

      if (value === null || value === undefined) {
        continue;
      }

      const parsed = Number(value);

      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    return 0;
  }

  private toPascalCase(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  private toSnakeCase(value: string): string {
    return value.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
  }

  private toKebabCase(value: string): string {
    return value.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
  }
}
